import { TFunction } from 'i18next';
import { z } from 'zod';

import {
  areEmailDomainsValid,
  formatEmailDomains,
} from '@bvi/api-interfaces/email-domain';
import {
  AccountStatus,
  PaymentMethod,
} from '@bvi/api-interfaces/entity/client';

export const buildValidationSchema = (t: TFunction) => {
  const dateValue = z
    .number({
      invalid_type_error: t('error.form.required'),
      required_error: t('error.form.required'),
    })
    .or(z.date());

  const requiredText = z.string().min(1, t('error.form.required')).default('');
  const requiredEmail = z
    .string()
    .min(1, t('error.form.required'))
    .email(t('error.form.email'))
    .default('');

  return z
    .object({
      name: requiredText,
      contactPerson: requiredText,
      contactEmail: requiredEmail,
      signatoryName: requiredText,
      signatoryEmail: requiredEmail,
      billingContactName: requiredText,
      billingContactEmail: requiredEmail,
      accountManagerName: requiredText,
      accountManagerEmail: requiredEmail,
      supportLiaisonName: requiredText,
      supportLiaisonEmail: requiredEmail,
      emailDomain: z
        .string()
        .min(1, t('error.form.required'))
        .transform((value) => formatEmailDomains(value))
        .refine((value) => areEmailDomainsValid(value), {
          message: t('error.form.emailDomain'),
        }),
      companySizeId: z.number({
        invalid_type_error: t('error.form.required'),
        required_error: t('error.form.required'),
      }),
      subscriptionStartsAt: dateValue,
      subscriptionExpiresAt: dateValue,
      subscriptionTermMonths: z.coerce
        .number({
          invalid_type_error: t('error.form.required'),
          required_error: t('error.form.required'),
        })
        .int()
        .min(1, t('error.form.required')),
      paymentMethod: z.nativeEnum(PaymentMethod, {
        required_error: t('error.form.required'),
        invalid_type_error: t('error.form.required'),
      }),
      paymentMethodNotes: z.string().optional().nullable(),
      accountStatus: z.nativeEnum(AccountStatus, {
        required_error: t('error.form.required'),
        invalid_type_error: t('error.form.required'),
      }),
      pricingNarrative: z.string().optional().nullable(),
      orientationAt: z.union([z.number(), z.date()]).optional().nullable(),
    })
    .refine(
      (values) =>
        new Date(values.subscriptionStartsAt).getTime() <=
        new Date(values.subscriptionExpiresAt).getTime(),
      {
        message: t('error.form.startBeforeEnd'),
        path: ['subscriptionExpiresAt'],
      },
    )
    .refine(
      (values) =>
        values.paymentMethod !== PaymentMethod.Other ||
        Boolean(values.paymentMethodNotes?.trim()),
      {
        message: t('error.form.required'),
        path: ['paymentMethodNotes'],
      },
    );
};
